<script setup>
  import { onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
  import { Button } from "@/components/ui/button";
  import { LoadingOverlay } from "@/components/episto-ui/loading-overlay";
  import GeneralSetupSection from "@/components/Survey/Setup/Sections/GeneralSetupSection.vue";
  import ContractCreditsSetupSection from "@/components/Survey/Setup/Sections/ContractCreditsSetupSection.vue";
  import { useSurveyDesignStore } from "@stores/survey/setup/surveyDesign";
  import { useSurveyTitleStore } from "@stores/survey/setup/surveyTitle";
  import { useSurveyCreditsStore } from "@stores/survey/setup/surveyCredits";
  import { useInitializeSurveyButton } from "@utils/composables/initializeSurveyButton";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useAccountsStore } from "@stores/accounts";
  import { useUsersStore } from "@stores/users";
  import { useCurrentUserStore } from "@stores/currentUser";

  const { startFromDefault } = useSurveyStore();
  const accountsStore = useAccountsStore();
  const usersStore = useUsersStore();
  const currentUserStore = useCurrentUserStore();
  const { isAdmin, userAccountIds, getUser } = storeToRefs(currentUserStore);

  const surveyDesignStore = useSurveyDesignStore();

  const { handleClick, isGenerating, loadingText } = useInitializeSurveyButton();

  const { isPromptEmpty, isFileMissing, isTemplateMissing } = storeToRefs(surveyDesignStore);

  const surveyTitleStore = useSurveyTitleStore();
  const { isTitleEmpty } = storeToRefs(surveyTitleStore);

  const surveyCreditsStore = useSurveyCreditsStore();
  const { isContractExhausted, isContractMissing } = storeToRefs(surveyCreditsStore);

  onMounted(async () => {
    const survey = await startFromDefault();
    survey.owner_id = getUser.value.id;
    await accountsStore.fetch();
    if (isAdmin.value) await usersStore.fetch({ account: userAccountIds.value });
  });
</script>

<template>
  <div
    class="flex justify-center p-4"
    data-cy="new-survey-page">
    <Card class="w-full max-w-2xl p-6">
      <CardHeader>
        <CardTitle class="heading-page">New survey</CardTitle>
        <CardDescription>Let's start with some basic information about your survey.</CardDescription>
      </CardHeader>
      <CardContent class="space-y-6">
        <GeneralSetupSection />
        <ContractCreditsSetupSection />
        <Button
          :disabled="
            isTitleEmpty ||
            isPromptEmpty ||
            isFileMissing ||
            isTemplateMissing ||
            isContractExhausted ||
            isContractMissing
          "
          @click="handleClick">
          OK
        </Button>
      </CardContent>
    </Card>
    <LoadingOverlay
      v-if="isGenerating"
      :text="loadingText" />
  </div>
</template>
